import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Button,
  Modal,
  Tag,
  Space,
  Tooltip,
  Typography,
  Upload as UploadAntd,
  Avatar,
  Col,
  Row,
  Tabs,
  Input,
} from "antd";
import { UserOutlined, TeamOutlined, EyeOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import lang from "../helper/lang";
import CheckImgIcon from "../assets/images/activee-main-icon.svg";
import apiPath from "../constants/apiPath";
import moment, { period } from "moment";
import RolePermissionsModal from "../components/RolePermissionModal";
import useDebounce from "../hooks/useDebounce";

const { Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

function ActiveSession() {
  const { request } = useRequest();
  // State management
  const [sessions, setSession] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });

  // Loading states for different operations
  const [deleteLoading, setDeleteLoading] = useState({});
  const [statusToggleLoading, setStatusToggleLoading] = useState({});

  const [viewSession, setViewSession] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);

  const isInitialRender = useRef(true);

  const [roleModalVisible, setRoleModalVisible] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const [activeTab, setActiveTab] = useState("user");

  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 500);

  const mapStatusToBoolean = (status) => {
    return status === "Active" || status === "active";
  };

  // Fetch sessions
  const fetchSession = useCallback(() => {
    setLoading(true);

    const params = new URLSearchParams();
    params.append("page", pagination.current);
    params.append("limit", pagination.pageSize);

    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    const url =
      activeTab === "admin"
        ? `/auth/admin/sessions?${params.toString()}`
        : `/auth/sessions?${params.toString()}`;

    request({
      url,
      method: "GET",
      onSuccess: (data) => {
        try {
          const sessionData = Array.isArray(data?.data) ? data.data : [];
          const meta = data?.pagination || data?.data?.pagination || {};

          setSession(sessionData);

          setPagination((prev) => ({
            ...prev,
            total: meta.total || sessionData.length,
          }));
        } catch (err) {
          handleApiError(err, "Failed to process session data");
          setSession([]);
        } finally {
          setLoading(false);
        }
      },
      onError: (error) => {
        setLoading(false);
        setSession([]);
        handleApiError(error, "Failed to fetch sessions");
      },
    });
  }, [activeTab, pagination.current, pagination.pageSize, debouncedSearchText, request]);

  useEffect(() => {
    fetchSession();
  }, [activeTab, pagination.current, pagination.pageSize, debouncedSearchText]);

  // Handlers
  const handleTableChange = (paginationInfo, sorter) => {
    setPagination((prev) => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize,
    }));
  };
  // --------------- Handle Terminate ------------
  const handleTerminateSession = (session) => {
    if (!session?.id) {
      ShowToast("Invalid session selected", Severty.ERROR);
      return;
    }

    const sessionId = session.id;

    const url =
      activeTab === "admin"
        ? `/auth/admin/sessions/${sessionId}/terminate`
        : `/auth/sessions/${sessionId}/terminate`;

    // 🔥 set loading per row
    setStatusToggleLoading((prev) => ({
      ...prev,
      [sessionId]: true,
    }));

    request({
      url,
      method: "POST",
      onSuccess: () => {
        ShowToast("Session terminated successfully", Severty.SUCCESS);

        setStatusToggleLoading((prev) => ({
          ...prev,
          [sessionId]: false,
        }));

        fetchSession();
      },
      onError: (error) => {
        setStatusToggleLoading((prev) => ({
          ...prev,
          [sessionId]: false,
        }));

        handleApiError(error, "Failed to terminate session");
      },
    });
  };

  const handleApiError = (error, fallback = "Something went wrong") => {
    console.error("API ERROR:", error);

    const message =
      error?.response?.data?.message ||
      error?.response?.message ||
      error?.message ||
      fallback;

    ShowToast(message, Severty.ERROR);
  };

  const InfoRow = ({ label, value, code, icon }) => (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      {icon && (
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            background: "rgba(37,99,235,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#2563eb",
            fontSize: 16,
          }}
        >
          {icon}
        </div>
      )}

      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {label}
        </Text>
        <div style={{ marginTop: 2 }}>
          {code ? <Text code>{value}</Text> : <Text>{value}</Text>}
        </div>
      </div>
    </div>
  );

  // Table columns
  const getColumns = () => [
    {
      title: "S. No.",
      key: "serial_no",
      width: 80,
      render: (_, __, index) => {
        const serialNumber =
          (pagination.current - 1) * pagination.pageSize + index + 1;
        return <Text>{serialNumber}</Text>;
      },
    },
    {
      title: lang("User_ID") || "User ID",
      dataIndex: ["user", "id"],
      key: "userId",
      width: 120,
      render: (id) => <Text code>{id}</Text>,
    },
    {
      title: lang("Name") || "Name",
      dataIndex: ["user", "full_name"],
      key: "name",
      width: 200,
      sorter: (a, b) => {
        const nameA = (a?.user?.first_name ?? "") + " " + (a?.user?.middle_name ?? "") + " " + (a?.user?.last_name ?? "") || "";
        const nameB = (b?.user?.first_name ?? "") + " " + (b?.user?.middle_name ?? "") + " " + (b?.user?.last_name ?? "") || "";
        return nameA.localeCompare(nameB);
        //   const nameA = a?.user?.full_name?.toLowerCase() || "";
        //   const nameB = b?.user?.full_name?.toLowerCase() || "";
        //   return nameA.localeCompare(nameB);
      },
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Avatar
            icon={<UserOutlined />}
            src={record?.user?.profile_img}
            style={{ backgroundColor: "#1890ff" }}
          />
          <span>{(record?.user?.first_name ?? "") + " " + (record?.user?.middle_name ?? "") + " " + (record?.user?.last_name ?? "") || "N/A"}</span>
        </div>
      ),
    },
    {
      title: lang("Email") || "Email",
      dataIndex: "user_email",
      key: "user_email",
      width: 200,
      render: (email) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{email || "N/A"}</span>
        </div>
      ),
    },
    {
      title: lang("Last_Login") || "Last Login",
      dataIndex: "last_activity",
      key: "last_activity",
      width: 150,
      sorter: (a, b) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (date) =>
        date ? moment(date).format("DD MMM YYYY, hh:mm A") : "-",
    },
    {
      title: "IP Address",
      dataIndex: "ip_address",
      key: "ip_address",
      width: 160,
      render: (ip) => <Text code>{ip || "N/A"}</Text>,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      width: 160,
      render: (location) => {
        const text = location || "N/A";

        return (
          <Tooltip title={text}>
            <span
              style={{
                display: "inline-block",
                maxWidth: "140px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                verticalAlign: "middle",
                cursor: "pointer"
              }}
            >
              {text}
            </span>
          </Tooltip>
        );
      }
    },
    {
      title: lang("Status") || "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      render: (status) => (
        <Tag color={status ? "green" : "red"}>
          {status ? "Active" : "In-Active"}
        </Tag>
      ),
    },
    {
      title: lang("Actions") || "Actions",
      key: "actions",
      width: 250,
      render: (_, record) => (
        <Space size="small">
          {/* <Tooltip title={lang("View_Profile") || "View Profile"}>
            <Button
              className="button-action-small"
              icon={<Eye />}
              onClick={() => handleViewProfile(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip>
          <Tooltip title={lang("Edit") || "Edit"}>
            <Button
              className="button-action-small"
              icon={<Pencil />}
              onClick={() => handleEditUser(record)}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
            />
          </Tooltip> */}
          <Tooltip title="View Session">
            <Button
              className="button-action-small"
              icon={<EyeOutlined />}
              onClick={() => {
                setViewSession(record);
                setViewModalVisible(true);
              }}
            />
          </Tooltip>
          <Tooltip title={lang("Terminate") || "Terminate"}>
            <Button
              className="button-action-small"
              icon={<img src={CheckImgIcon} alt="terminate" />}
              style={{ color: "#52c41a" }}
              loading={statusToggleLoading[record.id]}
              disabled={
                !record.is_active || statusToggleLoading[record.id]
              }
              onClick={() =>
                showSessionStatusConfirm(
                  record,
                  false,
                  handleTerminateSession,
                  statusToggleLoading,
                  deleteLoading,
                )
              }
            />
          </Tooltip>
          {/* <Tooltip title={lang("Delete") || "Delete"}>
            <Button
              className="button-action-small"
              icon={<Trash2 />}
              style={{ color: "#ff4d4f" }}
              loading={deleteLoading[record._id || record.id]}
              disabled={
                statusToggleLoading[record._id || record.id] ||
                deleteLoading[record._id || record.id]
              }
              onClick={() => {
                setUserToDelete(record);
                setDeleteModalVisible(true);
              }}
            />
          </Tooltip> */}
        </Space>
      ),
    },
  ];

  // Terminate Session status confirm
  const showSessionStatusConfirm = (record) => {
    if (!record) return;

    confirm({
      title: "Are you sure you want to terminate this session?",
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk() {
        handleTerminateSession(record);
      },
    });
  };

  const getRolePermissions = (roleId) => {
    request({
      url: `/api/admin/roles/${roleId}`,
      method: "GET",
      onSuccess: (data) => {
        setSelectedRole(data?.data);
        setRoleModalVisible(true);
      },
      onError: (error) => {
        handleApiError(error, "Failed to get role permissions");
        setRoleModalVisible(false);
        setSelectedRole(null);
      },
    });
  };

  //---------- Main Table -----------
  return (
    <div>
      <Card
        title={
          <>
            <TeamOutlined style={{ marginRight: 8 }} />
            {"Active Session"}
            <p>
              Admin manages end-user accounts: view, edit, terminate or delete
              as necessary.
            </p>
          </>

        }
      >
        {/* sessions Table */}
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
          items={[
            { key: "user", label: "User Sessions" },
            { key: "admin", label: "Admin Sessions" },
          ]}
        />

        <div style={{ marginBottom: 16 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
            }}
          >
            <>
              <Search
                placeholder={
                  lang("Search_by_name_or_email") || "Search by name or email"
                }
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                // onSearch={() => fetchSession()}
                style={{ minWidth: 200, flex: "1 1 200px" }}
              />
              <Button
                icon={<ExclamationCircleOutlined />}
                disabled={searchText === ""}
                onClick={() => {
                  setSearchText("");
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
              >
                {lang("Clear_Filters") || "Clear Filters"}
              </Button>
            </>

          </div>
        </div>

        <div className="table-responsive">
          <Table
            columns={getColumns()}
            dataSource={Array.isArray(sessions) ? sessions : []}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total || 0,
              showSizeChanger: true,
              showQuickJumper: false,
              showTotal: (total, range) =>
                `${lang("Showing") || "Showing"} ${range[0]}-${range[1]} ${lang("of") || "of"
                } ${total} ${lang("sessions") || "sessions"}`,
              pageSizeOptions: ["10", "20", "30", "50"],
            }}
            locale={{
              emptyText: loading
                ? "Loading sessions..."
                : "No active sessions found",
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
      </Card>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <EyeOutlined style={{ color: "#1890ff" }} />
            Session Details
          </div>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setViewSession(null);
        }}
        footer={[
          <Button
            key="terminate"
            danger
            type="primary"
            icon={
              <img src={CheckImgIcon} alt="terminate" style={{ width: 14 }} />
            }
            onClick={() =>
              showSessionStatusConfirm(
                viewSession,
                false,
                handleTerminateSession,
                statusToggleLoading,
                deleteLoading,
              )
            }
          >
            Terminate Session
          </Button>,
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={900}
        centered
      >
        {viewSession?.user && (
          <div>
            {/* ===== Header / Hero ===== */}
            <Card
              style={{
                marginBottom: 24,
                borderRadius: 16,
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                color: "#fff",
              }}
              bodyStyle={{ padding: 28 }}
            >
              <Row align="middle" justify="space-between">
                <Col>
                  <Space size={16}>
                    <Avatar
                      size={72}
                      src={viewSession.user?.profile_img}
                      icon={<UserOutlined />}
                      style={{
                        background: "#2563eb",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                      }}
                    />
                    <div>
                      <Typography.Title
                        level={3}
                        style={{ color: "#fff", margin: 0 }}
                      >
                        {viewSession.user?.full_name}
                      </Typography.Title>
                      <Text style={{ color: "rgba(255,255,255,0.7)" }}>
                        {viewSession.user?.email}
                      </Text>
                      <div style={{ marginTop: 8 }}>
                        <Tag color="blue">
                          {viewSession.user?.role?.name || "No Role"}
                        </Tag>
                        <Tag color={viewSession.is_active ? "green" : "red"}>
                          {viewSession.is_active ? "Active" : "Inactive"}
                        </Tag>
                      </div>
                    </div>
                  </Space>
                </Col>
                <Col>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                      gap: 6,
                    }}
                  >
                    <Tag
                      style={{
                        background: "rgba(255,255,255,0.08)",
                        color: "#bae6fd",
                        border: "none",
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 999,
                        letterSpacing: 0.6,
                      }}
                    ></Tag>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 14px",
                        borderRadius: 12,
                        background:
                          "linear-gradient(135deg, rgba(14,165,233,0.25), rgba(2,132,199,0.25))",
                        border: "1px solid rgba(56,189,248,0.35)",
                        boxShadow: "0 10px 30px rgba(14,165,233,0.25)",
                      }}
                    >
                      <EyeOutlined style={{ color: "#7dd3fc", fontSize: 14 }} />

                      <Text
                        style={{
                          color: "#e0f2fe",
                          fontSize: 13,
                          fontWeight: 600,
                          letterSpacing: 0.4,
                        }}
                      >
                        Session ID: {viewSession.id}
                      </Text>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>

            {/* ===== Details Grid ===== */}
            <Row gutter={[24, 24]}>
              {/* Session Info */}
              <Col span={24} md={12}>
                <Card
                  title="Session Information"
                  bordered={false}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-1px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <Space direction="vertical" size="middle">
                    <InfoRow
                      label="IP Address"
                      value={viewSession?.ip_address || "N/A"}
                      code
                    />
                    <InfoRow
                      label="Last Activity"
                      value={moment(viewSession.last_activity).format(
                        "DD MMM YYYY, hh:mm A",
                      )}
                    />
                    <InfoRow
                      label="Session Created"
                      value={moment(viewSession.created_at).format(
                        "DD MMM YYYY, hh:mm A",
                      )}
                    />
                  </Space>
                </Card>
              </Col>

              {/* Device Info */}
              <Col span={24} md={12}>
                <Card
                  title="Device Information"
                  bordered={false}
                  style={{
                    borderRadius: 14,
                    boxShadow: "0 6px 20px rgba(0,0,0,0.06)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.transform = "translateY(-1px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                >
                  <InfoRow label="User Agent" value={viewSession.user_agent} />
                </Card>
              </Col>
            </Row>

            {/* ===== Role & Permissions ===== */}
            {activeTab === "admin" && <Card
              size="small"
              title="Role & Permissions"
              style={{
                background: "#f8fafc",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                marginTop: 24,
              }}
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Text style={{ color: "#64748b", fontSize: 13 }}>
                  Assigned Role
                </Text>

                <Space align="center" size="middle">
                  <Tag
                    color="blue"
                    style={{
                      fontSize: 14,
                      padding: "6px 14px",
                      borderRadius: 999,
                      fontWeight: 500,
                    }}
                  >
                    {viewSession.user?.role_name || "N/A"}
                  </Tag>

                  <Tooltip title="View role permissions">
                    <Button
                      shape="circle"
                      icon={<EyeOutlined />}
                      onClick={() => {
                        getRolePermissions(viewSession.user.role);
                      }}
                      style={{
                        background: "#0ea5e9",
                        color: "#fff",
                        border: "none",
                        boxShadow: "0 6px 16px rgba(14,165,233,0.35)",
                      }}
                    />
                  </Tooltip>
                </Space>
              </Space>
            </Card>}
          </div>
        )}
      </Modal>
      <RolePermissionsModal
        open={roleModalVisible}
        role={selectedRole}
        onClose={() => {
          setRoleModalVisible(false);
          setSelectedRole(null);
        }}
      />
    </div>
  );
}

export default ActiveSession;
